import { TReducers } from "@features/reducers";
import { IWalletDetails } from "@market/interfaces/IWallet";
import { Col, Row, Table } from "antd";
import * as React from "react";
import { connect } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { avatarBgColor, fundsColumns } from "./table-column";
import Chart, { mainCurrenciesArray } from "./parts/Chart/Chart";
import Big from "big.js";
import CurrencyDetails from "./parts/CurrencyDetails";
import { TUserAgreements } from "@features/reducer.info";
import { RiskModalProps } from "../../utils";

interface Props extends RouteComponentProps<{}> {
  supportedUserWallets: IWalletDetails[];
  tier: string;
  usResident: boolean;
  investorId: number;
  agreements: TUserAgreements;
  country: string;
  setRiskModalProps: (props: RiskModalProps) => void;
  canStake?: boolean;
}
import * as style from "./FundsTabStyles.module.scss";
const currencyOrder = ["btc", "eth", "staked_eth", "bfusd", "bft"];
const big = Big();

const sortData = (
  data: Pick<IWalletDetails, "total_usd" | "currency_name" | "currency">[],
  order: string[]
) => {
  const dataMap: {
    [key: string]: Pick<
      IWalletDetails,
      "total_usd" | "currency_name" | "currency"
    >;
  } = {};
  data.forEach((item) => {
    dataMap[item.currency] = item;
  });

  const sortedData = order.map((currency) => {
    return dataMap[currency] || { currency, total_usd: 0 };
  });

  const remainingData = data.filter((item) => !order.includes(item.currency));

  return [...sortedData, ...remainingData];
};

const FundsTab = ({
  supportedUserWallets,
  location,
  tier,
  usResident,
  investorId,
  agreements,
  country,
  canStake,
  setRiskModalProps,
}: Props) => {
  const chartRef = React.useRef<HTMLDivElement>(null);
  const [legendHeight, setLegendHeight] = React.useState(0);

  const isUk = React.useMemo(() => country === "GB", [country]);

  const isUkRiskModalEnabled = React.useMemo(
    () => isUk && process.env.UK_RISK_MODAL_ENABLED?.toLowerCase() === "true",
    [isUk]
  );

  if (!supportedUserWallets) return null;

  const data = supportedUserWallets?.map((balance) => ({
    total_usd: balance.total_usd,
    currency_name: balance.currency_name,
    currency: balance.currency,
  }));

  const dataSortedByMainCurrenctis = sortData(data, mainCurrenciesArray);

  let tableData = supportedUserWallets;

  const walletsWithAmount = tableData.filter(
    (wallet) => !Big(wallet.total_usd ?? 0).eq(0)
  );
  const walletsWithoutAmount = tableData
    .filter((wallet) => Big(wallet.total_usd ?? 0).eq(0))
    .slice()
    .sort((a, b) => {
      const aIndex = currencyOrder.indexOf(a.currency);
      const bIndex = currencyOrder.indexOf(b.currency);

      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;

      return aIndex - bIndex;
    });

  const colors = [
    ...dataSortedByMainCurrenctis
      .filter((x) => mainCurrenciesArray.includes(x.currency))
      .map((x) => avatarBgColor[x.currency]),
    avatarBgColor.default,
  ];
  const rowExpandable = ["staked_eth"];

  React.useEffect(() => {
    const calculateLegendHeight = () => {
      if (chartRef.current) {
        const legend = chartRef.current.querySelector(".apexcharts-legend");
        const height = legend ? legend.clientHeight : 0;
        setLegendHeight(height);
      }
    };

    const timer = setTimeout(calculateLegendHeight, 300);

    return () => clearTimeout(timer);
  }, []);

  const additionalPadding = React.useMemo(() => {
    return legendHeight > 0 ? legendHeight + 24 : 0;
  }, [legendHeight]);

  return (
    <>
      <Row>
        <Col span={17} xs={24} sm={24} lg={17}>
          <Table
            rowKey={"id"}
            columns={fundsColumns({
              location,
              tier,
              usResident,
              agreements,
              isUkRiskModalEnabled,
              isUk,
              setRiskModalProps,
              isUserCanStake: canStake,
            })}
            loading={!supportedUserWallets.length}
            dataSource={[...walletsWithAmount, ...walletsWithoutAmount]}
            className={style.table}
            expandable={{
              showExpandColumn: false,
              defaultExpandAllRows: true,
              expandRowByClick: false,
              fixed: true,
              expandedRowRender: (record) => (
                <CurrencyDetails
                  isUk={isUk}
                  currency={record.currency}
                  investorId={investorId}
                  tier={tier}
                />
              ),
              rowExpandable: (record) =>
                rowExpandable.includes(record.currency),
            }}
            pagination={false}
            scroll={{ y: 620 }}
          />
        </Col>
        <Col span={7} xs={24} sm={24} lg={7} ref={chartRef}>
          {supportedUserWallets?.length !== 0 && (
            <div
              className={style.chart}
              style={{ paddingBottom: additionalPadding }}
            >
              <p className={style.header}>Funds value</p>
              {!supportedUserWallets.some((x) => x.total_usd === null) ? (
                <Chart
                  data={dataSortedByMainCurrenctis}
                  sum={supportedUserWallets.reduce(
                    (prev: number, curr: IWalletDetails) => {
                      return Number(big(prev).add(curr.total_usd || 0));
                    },
                    0
                  )}
                  colors={colors}
                />
              ) : (
                <p className={style.chartErrorMsg}>
                  Data temporary unavailable
                </p>
              )}
            </div>
          )}
        </Col>
      </Row>
    </>
  );
};

const mapStateToProps = (store: TReducers) => ({
  supportedUserWallets: store.fundsReducer.supportedUserWallets?.filter(
    (wallet) => !wallet.public_equity_shares
  ),
  companySlug: store.companyReducer.companyDetails.slug,
  tier: store.userInfo.tier,
  usResident: store.userInfo.us_resident,
  investorId: store.userInfo.investor_id,
  agreements: store.userInfo.agreements,
  country: store.userInfo.country,
  canStake: store.userInfo.can_stake,
});

export default withRouter(connect(mapStateToProps)(FundsTab));
